/**
 * @bos/portal3 — the BUILD DOOR of one app (on the app page): a description box, a Build
 * button, and every build this app has been through.
 *
 * It builds ONE app — the app already has its name, so this form only asks what the app
 * should do. Naming happens once, on the project page, where a new app is named and built
 * in the same press (apps/AppsSection).
 *
 * THE CONFLICT IS INFORMATION, NOT AN ERROR: one live run per BUSINESS is the server's law
 * — the machine builds one thing at a time, and that has not changed now that a business
 * can have several apps. So a second Build press is answered with the run already going,
 * WHICHEVER app it belongs to: "something is already building for this business — here it
 * is", with the way in. Never red, never an apology. The same information is shown
 * proactively when the business's runs list already knows a live run.
 *
 * The task prose is sent BYTE-FOR-BYTE (no trim): the server persists it exactly and the
 * factory's replay-for-free depends on exact bytes. Blankness alone disables the button.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Run } from '@bos/portal3-server';
import { PATHS } from '../routes';
import { client } from '../rpc/client';
import {
  runErrorMessage,
  startConflictOf,
  useAppRuns,
  useProjectRuns,
  type StartConflict,
} from './hooks';
import { isTerminalStatus, statusLabel } from './model';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The badge modifier per run status (visual tone only; the words come from statusLabel). */
export function statusBadgeClass(status: Run['status']): string {
  switch (status) {
    case 'running':
      return 'badge--run-live';
    case 'awaiting-owner':
      return 'badge--run-waiting';
    case 'completed':
      return 'badge--run-done';
    case 'budget-stopped':
      // A deliberate stop at the owner's ceiling — the WAITING hue, never the failure red.
      return 'badge--run-waiting';
    case 'failed':
    case 'aborted':
      return 'badge--run-failed';
    default:
      return 'badge--run-idle';
  }
}

export function RunRow({ run }: { run: Run }) {
  // A build stopped ON THIS OWNER is the one row in the list that is asking for something.
  // Its badge already says so; the door it names is what makes the row actionable, so the
  // way in stops being the same quiet ghost button every finished build gets.
  const waiting = run.status === 'awaiting-owner';
  return (
    <li className={`run-row${waiting ? ' run-row--waiting' : ''}`}>
      <span className={`badge ${statusBadgeClass(run.status)}`}>{statusLabel(run.status)}</span>
      <span className="run-row__meta">started {formatDate(run.startedAt)}</span>
      {waiting && <span className="waitchip">⏸ it needs you</span>}
      <Link
        className={`btn ${waiting ? 'btn--primary' : 'btn--ghost'} btn--sm run-row__open`}
        to={PATHS.run(run.id)}
      >
        {waiting ? 'Go to it' : 'Open'}
      </Link>
    </li>
  );
}

/**
 * A run of THIS business that would refuse a second Build, rendered as the way into it.
 * Shared by this door and the project page's, so both say the same thing the same way.
 */
export function LiveRunNotice({
  conflict,
  liveRun,
}: {
  conflict: StartConflict | null;
  liveRun: Run | undefined;
}) {
  if (conflict === null && liveRun === undefined) return null;
  const runId = conflict?.runId ?? liveRun?.id ?? null;
  // Name the app the live run belongs to — it may not be the one being looked at. A run
  // older than apps has no app name, and is described as what it is instead.
  const whose =
    liveRun === undefined ? null : liveRun.appName === null ? 'an earlier build' : `“${liveRun.appName}”`;
  return (
    <div className="run-conflict" role="status">
      <p className="run-conflict__text">
        {conflict !== null
          ? 'Something is already building for this business — one at a time. Here it is:'
          : liveRun?.status === 'awaiting-owner'
            ? `${whose} is waiting for your answer.`
            : `${whose} is building right now.`}
      </p>
      {runId !== null && (
        <Link className="btn btn--primary btn--sm" to={PATHS.run(runId)}>
          Open the run
        </Link>
      )}
    </div>
  );
}

/**
 * Build this app again. `projectId` is only used to see the WHOLE business's live run —
 * the gate the server enforces spans every app, so a door that watched only its own app's
 * runs would offer a button the server was about to refuse.
 */
export function StartRunSection({ appId, projectId }: { appId: string; projectId: string }) {
  const navigate = useNavigate();
  const { runs, status: runsStatus, reload } = useAppRuns(appId);
  const { runs: businessRuns, reload: reloadBusiness } = useProjectRuns(projectId);

  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<StartConflict | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A run the server would refuse a second start over — anywhere in this business.
  const liveRun = businessRuns.find((r) => !isTerminalStatus(r.status));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setConflict(null);
    setBusy(true);
    try {
      // Byte-for-byte: the textarea's exact content is the task the factory hashes on.
      const run = await client.runs.start.mutate({ appId, projectTask: task });
      navigate(PATHS.run(run.id));
    } catch (err) {
      const asConflict = startConflictOf(err);
      if (asConflict !== null) {
        // Information, not an error: a run is already going — offer the way in.
        setConflict(asConflict);
        void reload();
        void reloadBusiness();
      } else {
        setError(runErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const built = runs.length > 0;

  return (
    <section className="card build-start" aria-label="Build this app">
      <h2 className="card__title">{built ? 'Build it again' : 'Build it'}</h2>
      <p className="card__intro">
        {built
          ? 'Describe what this app should do — including anything you want changed. Sorvel builds it again from scratch, and the earlier build stays where it is.'
          : 'Describe what this app should do, in your own words. Sorvel builds it, shows you every step, and stops to ask when a decision is yours to make.'}
      </p>

      <LiveRunNotice conflict={conflict} liveRun={liveRun} />

      <form className="form" onSubmit={(e) => void onSubmit(e)} aria-label="Start a build">
        {error && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
        <label className="form__field">
          <span className="form__label">What should it do?</span>
          <textarea
            className="form__textarea build-start__task"
            name="projectTask"
            placeholder="e.g. A point-of-sale for the bakery: products, daily stock, sales at the counter, end-of-day waste write-off…"
            value={task}
            onChange={(e) => setTask(e.target.value)}
            disabled={busy}
            rows={5}
          />
        </label>
        <div className="form__actions">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={busy || task.trim() === '' || liveRun !== undefined}
          >
            {busy ? 'Starting…' : 'Build'}
          </button>
        </div>
      </form>

      {runsStatus === 'ready' && runs.length > 0 && (
        <div className="build-start__history">
          <h3 className="build-start__history-title">Builds of this app</h3>
          <ul className="run-list">
            {runs.map((run) => (
              <RunRow key={run.id} run={run} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
